// Email folder preferences — the "Customize folders" dialog's two
// controls:
//   * `hidden`: folder identifiers (wellKnownName preferred, folder.id
//     fallback) the user has hidden from the EMAIL section. Hidden
//     folders still appear in the dialog so they can be re-shown.
//   * `order`: folder identifiers in the user's preferred render order.
//     Anything not in `order` falls back to the system-folders-first,
//     then-alphabetical sort.
//
// Storage: these follow the ACCOUNT, not the browser (2026-08-15).
// They lived in localStorage through v1.x, which meant an advisor who
// customized their folder list on a laptop opened a stock list on any
// other browser or machine — Grant hit exactly that and asked for the
// whole class of per-browser settings fixed. The shape now lives in the
// user-settings registry and rides the shared provider; see
// web/src/settings/user-settings-registry.ts for the descriptor and
// UserSettingsProvider for the read/write path. Existing browsers seed
// the account on first load, so no one's list resets.
//
// `applyPrefsToFolderIds` stays a pure function here — it's the actual
// sort algorithm, is unit-tested without a DOM, and has no reason to
// know where the prefs came from.

import { EMAIL_FOLDER_PREFS, type EmailFolderPrefs } from "../settings/user-settings-registry";
import { useUserSetting } from "../settings/user-settings-context";

/** Kept as the local name for this shape — the type is consumed widely
 *  enough in the email components that renaming it buys nothing. */
export type FolderPrefs = EmailFolderPrefs;

export interface UseFolderPrefsResult {
  prefs: FolderPrefs;
  /** Hide or re-show one folder. */
  setHidden: (id: string, hidden: boolean) => void;
  /** Replace the render order. De-duped on write. */
  setOrder: (order: string[]) => void;
  /** Back to the system default (nothing hidden, natural order). */
  reset: () => void;
}

export function useFolderPrefs(): UseFolderPrefsResult {
  const [prefs, setPrefs] = useUserSetting(EMAIL_FOLDER_PREFS);

  const setHidden = (id: string, hidden: boolean): void => {
    const next = new Set(prefs.hidden);
    if (hidden) next.add(id);
    else next.delete(id);
    setPrefs({ hidden: next, order: prefs.order });
  };

  const setOrder = (order: string[]): void => {
    setPrefs({
      hidden: prefs.hidden,
      order: order.filter((id, index) => order.indexOf(id) === index),
    });
  };

  const reset = (): void => {
    setPrefs({ hidden: new Set<string>(), order: [] });
  };

  return { prefs, setHidden, setOrder, reset };
}

// Apply hide + order to a flat list of folder ids in the natural
// (system-folders-first) order. Returns the visible list in the user's
// preferred order. Pure — exported so tests can exercise it without
// spinning up React or the settings provider.
export function applyPrefsToFolderIds(naturalOrder: string[], prefs: FolderPrefs): string[] {
  const visible = naturalOrder.filter((id) => !prefs.hidden.has(id));
  if (prefs.order.length === 0) return visible;

  // Honor user order for any id that's both in `order` and visible;
  // append anything visible-but-not-in-order at the end (so newly
  // discovered folders show up without an explicit save).
  const orderSet = new Set(prefs.order);
  const visibleSet = new Set(visible);
  const ordered: string[] = [];
  for (const id of prefs.order) {
    if (visibleSet.has(id)) ordered.push(id);
  }
  for (const id of visible) {
    if (!orderSet.has(id)) ordered.push(id);
  }
  return ordered;
}
